import React from 'react';
import { cn } from '@hero/web-lib';

export interface ChoiceCardProps {
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  /** Short name of the option. Node, so it can carry a badge or qualifier. */
  label: React.ReactNode;
  /** One line explaining the consequence of picking this option. */
  hint?: React.ReactNode;
  /** The option's payload — a price, an input, a summary. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * `<ChoiceCard>` — one selectable option in a "pick exactly one" group.
 *
 * A `radio` role rather than a button, so the mutual exclusivity is
 * announced rather than merely implied by the ring, and keyboard users get
 * the behaviour they expect from Enter / Space.
 *
 * Selecting is deliberately separate from committing: every consumer of this
 * pattern is asking the user to compare consequences (two prices, two
 * accuracy outcomes), which they can't do if the first click already wrote
 * something. Pair it with `ModalForm`'s Save/Cancel footer.
 *
 * The selected tint uses `bg-primary-50` with **no** opacity modifier —
 * `primary-50` is defined with its own alpha (`#3453D914`), and a modifier
 * on top replaces that alpha instead of reducing it, which renders as a
 * saturated half-opaque `primary-600` that swallows small print.
 */
export const ChoiceCard: React.FC<ChoiceCardProps> = ({
  selected,
  onSelect,
  disabled,
  label,
  hint,
  children,
  className,
}) => (
  <div
    role="radio"
    aria-checked={selected}
    tabIndex={disabled ? -1 : 0}
    onClick={() => !disabled && onSelect()}
    onKeyDown={e => {
      if (disabled) return;
      // Only the card's own keystrokes select it. Without the target check,
      // a card holding an input would eat the Space key its own field needs.
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    }}
    className={cn(
      'flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors',
      selected ? 'border-primary-300 bg-primary-50' : 'border-slate-200 hover:border-slate-300',
      disabled && 'cursor-not-allowed opacity-60',
      className,
    )}>
    <span
      className={cn(
        'mt-1 flex size-4 shrink-0 items-center justify-center rounded-full border',
        selected ? 'border-primary-500' : 'border-slate-300',
      )}>
      {selected ? <span className="size-2 rounded-full bg-primary-500" /> : null}
    </span>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
      {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
    </div>
  </div>
);
